// Frame thumbnail gallery with selection, filtering, and batch actions
import { useState, useRef, useEffect } from 'react'
import { CapturedFrame, FrameFilterOptions } from '@/features/frames/frames-store'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { 
  Search, 
  Download, 
  Trash2, 
  CheckSquare, 
  Square, 
  Filter,
  SortAsc,
  SortDesc,
  MoreVertical,
  X,
  Image as ImageIcon,
  Clock,
  FileImage
} from 'lucide-react'
import { cn, formatFileSize } from '@/lib/utils'
import { useNavigate } from 'react-router-dom'
import { GifSessionSelector } from '@/components/GifSessionSelector'
import { useGifSessions } from '@/hooks/useGifSessions'
import { toast } from 'sonner'

// Format timestamp as mm:ss.ms
function formatTimestamp(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  const ms = Math.round((seconds % 1) * 100)
  return `${mins}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`
}

interface FrameGalleryProps {
  frames: CapturedFrame[]
  selectedIds: Set<string>
  highlightedFrameId?: string | null
  isLoading?: boolean
  onToggleSelection: (frameId: string) => void
  onSelectAll: () => void
  onSelectNone: () => void
  onDeleteSelected: () => Promise<void>
  onDeleteFrame: (frameId: string) => Promise<void>
  onDownloadSelected: () => Promise<void>
  onDownloadFrame: (frame: CapturedFrame) => void
  onFrameClick: (frame: CapturedFrame) => void
  onFrameHover?: (frame: CapturedFrame | null) => void
  filterOptions: FrameFilterOptions
  onFilterChange: (options: Partial<FrameFilterOptions>) => void
  onClearFilters: () => void
}

export function FrameGallery({
  frames,
  selectedIds,
  highlightedFrameId,
  isLoading,
  onToggleSelection,
  onSelectAll,
  onSelectNone,
  onDeleteSelected,
  onDeleteFrame,
  onDownloadSelected,
  onDownloadFrame,
  onFrameClick,
  onFrameHover,
  filterOptions,
  onFilterChange,
  onClearFilters
}: FrameGalleryProps) {
  const navigate = useNavigate()
  const { sessions, createSession, addFrameToSession, refreshSessions } = useGifSessions()
  const [searchTerm, setSearchTerm] = useState(filterOptions.searchTerm || '')
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [frameToDelete, setFrameToDelete] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showGifSessionSelector, setShowGifSessionSelector] = useState(false)
  const [isAddingToSession, setIsAddingToSession] = useState(false)
  const highlightedRef = useRef<HTMLDivElement>(null)

  // Refresh sessions when selector opens
  useEffect(() => {
    if (showGifSessionSelector) {
      refreshSessions()
    }
  }, [showGifSessionSelector, refreshSessions])

  // Scroll to highlighted frame
  useEffect(() => {
    if (highlightedFrameId && highlightedRef.current) {
      highlightedRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      })
    }
  }, [highlightedFrameId])

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== filterOptions.searchTerm) {
        onFilterChange({ searchTerm: searchTerm || undefined })
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm, filterOptions.searchTerm, onFilterChange])

  const isAllSelected = frames.length > 0 && frames.every(f => selectedIds.has(f.id))
  const hasSelection = selectedIds.size > 0
  const hasActiveFilters = filterOptions.searchTerm || 
    filterOptions.frameNumberMin !== undefined || 
    filterOptions.frameNumberMax !== undefined

  const handleDeleteClick = (frameId?: string) => {
    setFrameToDelete(frameId || null)
    setShowDeleteDialog(true)
  }

  const handleConfirmDelete = async () => {
    setIsDeleting(true)
    try {
      if (frameToDelete) {
        await onDeleteFrame(frameToDelete)
      } else {
        await onDeleteSelected()
      }
    } finally {
      setIsDeleting(false)
      setShowDeleteDialog(false)
      setFrameToDelete(null)
    }
  }

  const toggleSortOrder = () => {
    onFilterChange({ 
      sortOrder: filterOptions.sortOrder === 'asc' ? 'desc' : 'asc' 
    })
  }

  const handleCreateGif = () => {
    // Get selected frames data
    const selectedFrames = frames.filter(f => selectedIds.has(f.id))
    if (selectedFrames.length === 0) return

    setShowGifSessionSelector(true)
  }

  // Handle selecting a GIF session
  const handleSelectGifSession = async (session: { id: string; name: string }) => {
    const selectedFrames = frames.filter(f => selectedIds.has(f.id))
    if (selectedFrames.length === 0) return

    setIsAddingToSession(true)
    
    try {
      let addedCount = 0
      for (const frame of selectedFrames) {
        // Add each frame to the session with proper structure
        const success = await addFrameToSession(
          session.id,
          {
            sourceFrameId: frame.id,
            imageData: frame.imageData,
            width: frame.width || 640,
            height: frame.height || 360,
          }
        )
        
        if (success) addedCount++
      }

      if (addedCount > 0) {
        toast.success(`Added ${addedCount} frame${addedCount !== 1 ? 's' : ''} to "${session.name}"`)
        setShowGifSessionSelector(false)
        // Navigate to the GIF creator with the session
        navigate(`/?view=gif&session=${session.id}`)
      }
    } catch (error) {
      console.error('Error adding frames to session:', error)
      toast.error('Failed to add frames to session')
    } finally {
      setIsAddingToSession(false)
    }
  }

  // Handle creating a new session and adding frames
  const handleCreateAndSelectSession = async (name: string) => {
    const session = await createSession(name)
    if (session) {
      await handleSelectGifSession(session)
      return session
    }
    return null
  }

  if (frames.length === 0 && !isLoading && !hasActiveFilters) {
    return (
      <Card className="p-8 text-center">
        <ImageIcon className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium mb-2">No frames captured yet</h3>
        <p className="text-sm text-muted-foreground">
          Use the camera button to capture frames from the video
        </p>
      </Card>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search frames..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 h-9"
          />
          {searchTerm && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6"
              onClick={() => setSearchTerm('')}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>

        {/* Sort */}
        <div className="flex items-center gap-1">
          <Select
            value={filterOptions.sortBy || 'frameNumber'}
            onValueChange={(value) => onFilterChange({ sortBy: value as FrameFilterOptions['sortBy'] })}
          >
            <SelectTrigger className="w-[140px] h-9">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="frameNumber">Frame Number</SelectItem>
              <SelectItem value="captureDate">Capture Date</SelectItem>
              <SelectItem value="videoName">Video Name</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            onClick={toggleSortOrder}
          >
            {filterOptions.sortOrder === 'desc' ? (
              <SortDesc className="h-4 w-4" />
            ) : (
              <SortAsc className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Selection controls */}
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={isAllSelected ? onSelectNone : onSelectAll}
            className="h-9"
          >
            {isAllSelected ? (
              <>
                <Square className="h-4 w-4 mr-1" />
                None
              </>
            ) : (
              <>
                <CheckSquare className="h-4 w-4 mr-1" />
                All
              </>
            )}
          </Button>
        </div>

        {/* Batch actions */}
        {hasSelection && (
          <div className="flex items-center gap-1">
            <Badge variant="secondary" className="h-9 px-3">
              {selectedIds.size} selected
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCreateGif}
              className="h-9"
              title="Create animated GIF from selected frames"
            >
              <FileImage className="h-4 w-4 mr-1" />
              Create GIF
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onDownloadSelected}
              className="h-9"
            >
              <Download className="h-4 w-4 mr-1" />
              Download
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDeleteClick()}
              className="h-9 text-destructive hover:bg-destructive hover:text-destructive-foreground"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </Button>
          </div>
        )}

        {/* Clear filters */}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="h-9"
          >
            <Filter className="h-4 w-4 mr-1" />
            Clear Filters
          </Button>
        )}
      </div>

      {/* Frame count */}
      <div className="text-sm text-muted-foreground">
        {frames.length} frame{frames.length !== 1 ? 's' : ''}
        {hasActiveFilters && ' (filtered)'}
      </div>

      {/* Gallery grid */}
      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {[...Array(8)].map((_, i) => (
            <div 
              key={i} 
              className="aspect-video bg-muted animate-pulse rounded-lg"
            />
          ))}
        </div>
      ) : frames.length === 0 ? (
        <Card className="p-8 text-center">
          <Filter className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No frames match your filters</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Try adjusting your search or filter criteria
          </p>
          <Button variant="outline" onClick={onClearFilters}>
            Clear Filters
          </Button>
        </Card>
      ) : (
        <>
          {/* Mobile horizontal scroll view */}
          <div className="md:hidden overflow-x-auto -mx-4 px-4 pb-2 scrollbar-hide">
            <div className="flex gap-3" style={{ width: 'max-content' }}>
              {frames.map((frame) => {
                const isSelected = selectedIds.has(frame.id)
                const isHighlighted = highlightedFrameId === frame.id

                return (
                  <div
                    key={frame.id}
                    ref={isHighlighted ? highlightedRef : undefined}
                    className={cn(
                      'group relative rounded-lg overflow-hidden border-2 transition-all cursor-pointer flex-shrink-0',
                      'w-[140px]',
                      isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-transparent',
                      isHighlighted && 'border-yellow-400 ring-2 ring-yellow-200 scale-105 z-10'
                    )}
                    onClick={() => onFrameClick(frame)}
                    onTouchStart={() => onFrameHover?.(frame)}
                    onTouchEnd={() => onFrameHover?.(null)}
                  >
                    {/* Thumbnail */}
                    <div className="aspect-video bg-muted">
                      <img
                        src={frame.thumbnailData || frame.imageData}
                        alt={`Frame ${frame.frameNumber}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>

                    {/* Selection checkbox */}
                    <div 
                      className="absolute top-1 left-1 z-10"
                      onClick={(e) => {
                        e.stopPropagation()
                        onToggleSelection(frame.id)
                      }}
                    >
                      <Checkbox
                        checked={isSelected}
                        className={cn(
                          'h-5 w-5 border-2 bg-background/80 backdrop-blur-sm',
                          isSelected && 'border-blue-500'
                        )}
                      />
                    </div>

                    {/* Info overlay */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1.5">
                      <div className="flex items-center justify-between text-white text-xs">
                        <span className="font-medium">#{frame.frameNumber}</span>
                        <span className="opacity-80">
                          {formatTimestamp(frame.timestamp)}
                        </span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Desktop grid view */}
          <ScrollArea className="h-[400px] hidden md:block">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 pr-4">
              {frames.map((frame) => {
                const isSelected = selectedIds.has(frame.id)
                const isHighlighted = highlightedFrameId === frame.id

                return (
                  <div
                    key={frame.id}
                    ref={isHighlighted ? highlightedRef : undefined}
                    className={cn(
                      'group relative rounded-lg overflow-hidden border-2 transition-all cursor-pointer',
                      isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-transparent',
                      isHighlighted && 'border-yellow-400 ring-2 ring-yellow-200 scale-105 z-10'
                    )}
                    onClick={() => onFrameClick(frame)}
                    onMouseEnter={() => onFrameHover?.(frame)}
                    onMouseLeave={() => onFrameHover?.(null)}
                  >
                    {/* Thumbnail */}
                    <div className="aspect-video bg-muted">
                      <img
                        src={frame.thumbnailData || frame.imageData}
                        alt={`Frame ${frame.frameNumber}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>

                    {/* Selection checkbox */}
                    <div 
                      className="absolute top-2 left-2 z-10"
                      onClick={(e) => {
                        e.stopPropagation()
                        onToggleSelection(frame.id)
                      }}
                    >
                      <Checkbox
                        checked={isSelected}
                        className={cn(
                          'h-5 w-5 border-2 bg-background/80 backdrop-blur-sm',
                          isSelected && 'border-blue-500'
                        )}
                      />
                  </div>

                    {/* Frame info overlay */}
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-2 pt-6">
                      <div className="flex items-center justify-between text-white text-xs">
                        <span className="font-medium">#{frame.frameNumber}</span>
                        <span className="opacity-75">{formatFileSize(frame.fileSize)}</span>
                      </div>
                    </div>

                    {/* Hover actions */}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            variant="secondary" 
                            size="icon" 
                            className="h-7 w-7"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onDownloadFrame(frame)}>
                            <Download className="h-4 w-4 mr-2" />
                            Download
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleDeleteClick(frame.id)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    {/* Status badges */}
                    <div className="absolute top-2 right-10 flex gap-1">
                      {isHighlighted && (
                        <Badge variant="secondary" className="text-[10px] px-1 py-0 h-5 bg-yellow-400 text-yellow-900">
                          <Clock className="h-3 w-3" />
                        </Badge>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </ScrollArea>
        </>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {frameToDelete ? 'Frame' : 'Selected Frames'}?</AlertDialogTitle>
            <AlertDialogDescription>
              {frameToDelete
                ? 'This will permanently delete this captured frame.'
                : `This will permanently delete ${selectedIds.size} selected frame${selectedIds.size !== 1 ? 's' : ''}.`}
              <br />
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* GIF session selector */}
      <GifSessionSelector
        open={showGifSessionSelector}
        onOpenChange={setShowGifSessionSelector}
        sessions={sessions}
        onSelectSession={handleSelectGifSession}
        onCreateAndSelect={handleCreateAndSelectSession}
        isLoading={isAddingToSession}
        frameCount={selectedIds.size}
      />
    </div>
  )
}

// Compact version for sidebar or smaller spaces
export function FrameGalleryCompact({
  frames,
  selectedIds,
  highlightedFrameId,
  onToggleSelection,
  onFrameClick,
  onFrameHover
}: {
  frames: CapturedFrame[]
  selectedIds: Set<string>
  highlightedFrameId?: string | null
  onToggleSelection: (frameId: string) => void
  onFrameClick: (frame: CapturedFrame) => void
  onFrameHover?: (frame: CapturedFrame | null) => void
}) {
  if (frames.length === 0) {
    return (
      <div className="text-center py-4 text-sm text-muted-foreground">
        No frames captured
      </div>
    )
  }

  return (
    <div className="flex gap-2 overflow-x-auto pb-2">
      {frames.map((frame) => {
        const isSelected = selectedIds.has(frame.id)
        const isHighlighted = highlightedFrameId === frame.id

        return (
          <div
            key={frame.id}
            className={cn(
              'relative flex-shrink-0 w-20 rounded overflow-hidden border-2 cursor-pointer transition-all',
              isSelected ? 'border-blue-500' : 'border-transparent',
              isHighlighted && 'border-yellow-400 ring-1 ring-yellow-200'
            )}
            onClick={() => onFrameClick(frame)}
            onMouseEnter={() => onFrameHover?.(frame)}
            onMouseLeave={() => onFrameHover?.(null)}
          >
            <div className="aspect-video">
              <img
                src={frame.thumbnailData || frame.imageData}
                alt={`Frame ${frame.frameNumber}`}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10px] text-center py-0.5">
              #{frame.frameNumber}
            </div>
            <div 
              className="absolute top-1 left-1"
              onClick={(e) => {
                e.stopPropagation()
                onToggleSelection(frame.id)
              }}
            >
              <Checkbox
                checked={isSelected}
                className="h-4 w-4 bg-background/80"
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}
